import React, { useState, useEffect } from 'react';
import { Card, Collapse, IconButton, Grid, Divider, TableCell, TableRow } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import StageExpandableTable from '../tabels/StageExpandableTable';
import Icon from '@mui/material/Icon';
import { grey, red, green } from '@mui/material/colors';
import PropTypes from 'prop-types';
import { useVisibility } from 'context/visibilityContext'; // Import the hook
import { filterDiffsByDisplayColumns } from 'utils/visibilityFilter'; // Import the filter utility
import DiffRenderer from './DiffRenderer';

function ProductDetailsReview({ selectedOrg, productData, showSameFields }) {
  // Receive showSameFields
  const [expandedRow, setExpandedRow] = useState(null);
  const [rows, setRows] = useState([]);
  const { visibilityConfig, isLoadingConfig, configError } = useVisibility(); // Get context values

  const columns = [
    { name: 'product', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  useEffect(() => {
    if (productData && productData.length > 0) {
      console.log('GENERATED ROWS', productData);

      const generatedRows = productData.map((productDiff) => {
        return {
          id:
            productDiff.product?.sf_id ||
            productDiff.product?.external_id ||
            productDiff.product?.id ||
            productDiff.product?.sfId ||
            'missing-id',
          product: (
            <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
              {productDiff.product?.name ||
                productDiff.product.sf_product.IDFieldList.Name ||
                productDiff.product.sf_product.name ||
                'Unknown Product'}
            </ArgonTypography>
          ),
          sObjectName: 'Product2',
          diffs: productDiff.details,
        };
      });

      setRows(generatedRows);
    } else {
      setRows([]);
    }
  }, [productData]);

  const handleRowClick = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const renderExpandedContent = (row) => {
    return (
      <TableRow>
        <TableCell colSpan={columns.length} style={{ padding: '0px', borderBottom: 'none' }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
            <ArgonBox p={3} color="text" bgcolor={grey[50]}>
              {/* --- STEP 3: USE THE NEW COMPONENT --- */}
              <DiffRenderer
                changes={row.diffs || []}
                sObjectName={row.sObjectName}
                orgType={selectedOrg.type.toLowerCase()}
                showSameFields={showSameFields}
              />
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const formattedRows = rows.map((row) => ({
    ...row,
    renderExpandedContent: () => renderExpandedContent(row),
    onClick: () => handleRowClick(row.id),
    actions: (
      <IconButton
        aria-label="expand row"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleRowClick(row.id);
        }}
      >
        <Icon>{expandedRow === row.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
      </IconButton>
    ),
  }));

  const renderTableSection = (title) => (
    <ArgonBox mb={3}>
      <Card>
        {' '}
        {/* Added Card wrapper for consistency */}
        <ArgonBox p={3}>
          <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
            {title}
          </ArgonTypography>
          <ArgonBox
            sx={{
              '& .MuiTableRow-root:not(:last-child)': {
                '& td': {
                  borderBottom: ({ borders: { borderWidth, borderColor } }) => `${borderWidth[1]} solid ${borderColor}`,
                },
              },
            }}
          >
            <StageExpandableTable
              rows={formattedRows}
              columns={columns}
              renderRowSubComponent={({ row }) => row.renderExpandedContent()}
            />
          </ArgonBox>
        </ArgonBox>
      </Card>
    </ArgonBox>
  );

  return <ArgonBox>{renderTableSection('Product Details Changes')}</ArgonBox>;
}

ProductDetailsReview.propTypes = {
  productData: PropTypes.arrayOf(
    PropTypes.shape({
      details: PropTypes.arrayOf(
        PropTypes.shape({
          field: PropTypes.string.isRequired,
          oldValue: PropTypes.any,
          newValue: PropTypes.any,
          type: PropTypes.oneOf(['new', 'modified', 'deleted', 'same']).isRequired, // Include 'same'
        })
      ).isRequired,
      product: PropTypes.shape({
        name: PropTypes.string,
        sf_id: PropTypes.string,
        external_id: PropTypes.string,
        id: PropTypes.string, // Add id
      }),
    })
  ).isRequired,
  showSameFields: PropTypes.bool, // Add showSameFields prop type
};

export default ProductDetailsReview;
